import { useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import {
  Search, Loader2, Globe, Mail, Phone, MapPin, Code2,
  Instagram, Linkedin, Twitter, Youtube, Github,
  ExternalLink, Copy, AlertCircle, Sparkles, Link2,
  Users, Briefcase, Calendar, Target, Star, MessageCircle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import HistorySection from '../history/HistorySection';

interface SocialProfiles {
  instagram?: string; linkedin?: string; twitter?: string;
  facebook?: string; youtube?: string; tiktok?: string;
  github?: string; pinterest?: string;
  [key: string]: string | undefined;
}
interface PageSection { heading: string; content: string; }
interface WebIntelData {
  url: string;
  company: { name: string; description: string; industry?: string; logo?: string; favicon?: string };
  contact: { emails: string[]; phones: string[]; addresses: string[]; whatsapp?: string };
  social: SocialProfiles;
  seo: { title: string; metaDescription: string; keywords: string[]; ogImage?: string; language?: string };
  tech: string[];
  links: { text: string; href: string }[];
  sections: PageSection[];
  aiExtracted?: {
    summary: string;
    targetAudience?: string;
    servicesOffered?: string[];
    businessModel?: string;
    location?: string;
    foundedYear?: string;
    keyPeople?: string[];
    notableFacts?: string[];
    contactInsights?: string;
  };
}

const socialMeta: Record<string, { icon: any; color: string; label: string }> = {
  instagram: { icon: Instagram, color: '#e1306c', label: 'Instagram' },
  linkedin:  { icon: Linkedin,  color: '#0077b5', label: 'LinkedIn'  },
  twitter:   { icon: Twitter,   color: '#1da1f2', label: 'Twitter/X' },
  youtube:   { icon: Youtube,   color: '#ff0000', label: 'YouTube'   },
  github:    { icon: Github,    color: '#333',    label: 'GitHub'    },
  facebook:  { icon: Globe,     color: '#1877f2', label: 'Facebook'  },
  tiktok:    { icon: Globe,     color: '#010101', label: 'TikTok'    },
  pinterest: { icon: Globe,     color: '#e60023', label: 'Pinterest' },
};

const WebIntelligence = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WebIntelData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleAnalyze = async () => {
    const trimmed = url.trim();
    if (!trimmed) { setError('Please enter a website URL'); return; }
    setLoading(true); setError(null); setData(null);
    try {
      const res = await fetch('/api/tools/web-intel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: trimmed }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Analysis failed');
      setData(json);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const CopyBtn = ({ text, id }: { text: string; id: string }) => (
    <button onClick={() => copy(text, id)} className="ml-2 text-gray-400 hover:text-[#216974] transition-colors flex-shrink-0" title="Copy">
      {copiedKey === id ? <span className="text-xs text-[#54c4b6] font-bold">✓</span> : <Copy size={12} />}
    </button>
  );

  const InfoRow = ({ icon: Icon, label, children }: { icon: any; label: string; children: React.ReactNode }) => (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon size={15} className="text-gray-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">{label}</p>
        <div className="text-sm text-gray-800">{children}</div>
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex p-3 bg-gradient-to-br from-[#54c4b6] to-[#54c4b6] rounded-2xl shadow-lg shadow-cyan-200">
          <Globe size={32} className="text-white" />
        </div>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight">Website Intelligence</h1>
        <p className="text-gray-500 text-lg max-w-xl mx-auto font-medium">
          Paste any URL — extract company info, contacts, social profiles, tech stack, and AI-powered insights.
        </p>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-2xl shadow-cyan-100 overflow-hidden">
        <div className="p-6 space-y-3">
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                placeholder="https://example.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAnalyze()}
                className="h-14 text-lg pl-12 rounded-2xl"
                leftIcon={<Search className="text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" size={20} />}
              />
            </div>
            <Button
              onClick={handleAnalyze}
              isLoading={loading}
              className="px-10 h-14 rounded-2xl bg-gradient-to-r from-[#54c4b6] to-[#54c4b6] hover:scale-105 transition-transform text-white font-bold"
            >
              Analyze
            </Button>
          </div>
          {loading && (
            <div className="flex items-center gap-3 text-[#54c4b6] font-semibold text-sm animate-pulse">
              <Loader2 size={16} className="animate-spin" />
              Crawling page, extracting data, running AI analysis...
            </div>
          )}
          <AnimatePresence>
            {error && (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 font-semibold text-sm">
                <AlertCircle size={16} /> {error}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Card>

      <AnimatePresence>
        {data && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">

            {/* Company Banner */}
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-[#54c4b6] to-[#54c4b6]" />
              <div className="p-6 flex items-start gap-5">
                {data.company.favicon && (
                  <img src={data.company.favicon} alt="" className="w-14 h-14 rounded-xl object-contain border border-gray-100 shadow flex-shrink-0"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h2 className="text-2xl font-black text-gray-900">{data.company.name || 'Unknown'}</h2>
                    {data.aiExtracted?.businessModel && (
                      <span className="px-2.5 py-1 bg-[#54c4b6]/10 text-[#216974] rounded-full text-xs font-bold">{data.aiExtracted.businessModel}</span>
                    )}
                    {data.company.industry && (
                      <span className="px-2.5 py-1 bg-[#216974]/10 text-[#216974] rounded-full text-xs font-bold">{data.company.industry}</span>
                    )}
                  </div>
                  <a href={data.url} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-[#54c4b6] hover:underline flex items-center gap-1 mb-2">
                    <ExternalLink size={11} /> {data.url}
                  </a>
                  <p className="text-gray-600 text-sm leading-relaxed">{data.company.description}</p>
                  {(data.aiExtracted?.location || data.aiExtracted?.foundedYear) && (
                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 font-semibold">
                      {data.aiExtracted.location && <span className="flex items-center gap-1"><MapPin size={11} />{data.aiExtracted.location}</span>}
                      {data.aiExtracted.foundedYear && <span className="flex items-center gap-1"><Calendar size={11} />Founded {data.aiExtracted.foundedYear}</span>}
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* AI Summary + Key People + Services row */}
            {data.aiExtracted && (
              <div className="grid md:grid-cols-3 gap-6">
                {/* AI Summary */}
                <Card className="md:col-span-2 border-0 shadow-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="p-1.5 bg-[#216974] rounded-lg"><Sparkles size={16} className="text-[#216974]" /></div>
                    <h3 className="font-black text-gray-900">AI Intelligence Summary</h3>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-4">{data.aiExtracted.summary}</p>

                  {data.aiExtracted.targetAudience && (
                    <div className="flex items-start gap-2 mb-3 p-3 bg-[#216974] rounded-lg">
                      <Target size={14} className="text-[#216974] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-[10px] font-black text-[#216974] uppercase tracking-widest">Target Audience</p>
                        <p className="text-sm text-[#216974] font-medium">{data.aiExtracted.targetAudience}</p>
                      </div>
                    </div>
                  )}

                  {data.aiExtracted.servicesOffered && data.aiExtracted.servicesOffered.length > 0 && (
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Services / Products</p>
                      <div className="flex flex-wrap gap-2">
                        {data.aiExtracted.servicesOffered.map((s, i) => (
                          <span key={i} className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-semibold flex items-center gap-1">
                            <Briefcase size={10} /> {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>

                {/* Right column: Key People + Notable Facts */}
                <div className="space-y-4">
                  {data.aiExtracted.keyPeople && data.aiExtracted.keyPeople.length > 0 && (
                    <Card className="border-0 shadow-xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Users size={14} className="text-[#216974]" />
                        <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Key People</h3>
                      </div>
                      <div className="space-y-2">
                        {data.aiExtracted.keyPeople.map((person, i) => (
                          <div key={i} className="text-sm text-gray-800 font-medium flex items-start gap-1.5">
                            <span className="text-[#216974] mt-0.5">•</span>{person}
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}

                  {data.aiExtracted.notableFacts && data.aiExtracted.notableFacts.length > 0 && (
                    <Card className="border-0 shadow-xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Star size={14} className="text-[#216974]" />
                        <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Notable Facts</h3>
                      </div>
                      <div className="space-y-2">
                        {data.aiExtracted.notableFacts.map((fact, i) => (
                          <div key={i} className="text-sm text-gray-700 flex items-start gap-1.5">
                            <span className="text-[#e4c184] mt-0.5">★</span>{fact}
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                </div>
              </div>
            )}

            {/* Contact + Social + Tech */}
            <div className="grid md:grid-cols-3 gap-6">

              {/* Contact */}
              <Card className="border-0 shadow-xl p-6">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Contact Information</h3>
                <div className="space-y-5">
                  {data.contact.emails.length > 0 && (
                    <InfoRow icon={Mail} label="Email Addresses">
                      <div className="space-y-1">
                        {data.contact.emails.map((e, i) => (
                          <div key={i} className="flex items-center">
                            <a href={`mailto:${e}`} className="text-[#54c4b6] hover:underline truncate text-xs">{e}</a>
                            <CopyBtn text={e} id={`em${i}`} />
                          </div>
                        ))}
                      </div>
                    </InfoRow>
                  )}

                  {data.contact.phones.length > 0 && (
                    <InfoRow icon={Phone} label="Phone Numbers">
                      <div className="space-y-1">
                        {data.contact.phones.map((p, i) => (
                          <div key={i} className="flex items-center">
                            <a href={`tel:${p}`} className="text-gray-800 text-xs">{p}</a>
                            <CopyBtn text={p} id={`ph${i}`} />
                          </div>
                        ))}
                      </div>
                    </InfoRow>
                  )}

                  {data.contact.whatsapp && (
                    <InfoRow icon={MessageCircle} label="WhatsApp">
                      <div className="flex items-center">
                        <a href={`https://wa.me/${data.contact.whatsapp}`} target="_blank" rel="noopener noreferrer"
                          className="text-[#54c4b6] hover:underline text-xs font-semibold">{data.contact.whatsapp}</a>
                        <CopyBtn text={data.contact.whatsapp} id="wa" />
                      </div>
                    </InfoRow>
                  )}

                  {data.contact.addresses.length > 0 && (
                    <InfoRow icon={MapPin} label="Address">
                      {data.contact.addresses.map((a, i) => (
                        <p key={i} className="text-xs text-gray-700 leading-relaxed">{a}</p>
                      ))}
                    </InfoRow>
                  )}

                  {data.aiExtracted?.contactInsights && (
                    <InfoRow icon={AlertCircle} label="Contact Notes">
                      <p className="text-xs text-gray-600 leading-relaxed">{data.aiExtracted.contactInsights}</p>
                    </InfoRow>
                  )}

                  {!data.contact.emails.length && !data.contact.phones.length && !data.contact.addresses.length && (
                    <p className="text-gray-400 text-xs italic">No contact info found</p>
                  )}
                </div>
              </Card>

              {/* Social */}
              <Card className="border-0 shadow-xl p-6">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Social Profiles</h3>
                <div className="space-y-2">
                  {Object.entries(data.social).filter(([, v]) => v).map(([platform, href]) => {
                    const meta = socialMeta[platform] || { icon: Globe, color: '#6b7280', label: platform };
                    const Icon = meta.icon;
                    return (
                      <a key={platform} href={href} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 transition-colors group">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${meta.color}18` }}>
                          <Icon size={15} style={{ color: meta.color }} />
                        </div>
                        <span className="text-sm font-semibold text-gray-700 group-hover:text-[#54c4b6] truncate flex-1">{meta.label}</span>
                        <ExternalLink size={11} className="text-gray-300 group-hover:text-[#54c4b6]" />
                      </a>
                    );
                  })}
                  {!Object.values(data.social).some(Boolean) && (
                    <p className="text-gray-400 text-xs italic">No social profiles found</p>
                  )}
                </div>
              </Card>

              {/* Tech */}
              <Card className="border-0 shadow-xl p-6">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Tech Stack</h3>
                {data.tech.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {data.tech.map((t) => (
                      <span key={t} className="px-2.5 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-bold flex items-center gap-1.5">
                        <Code2 size={10} /> {t}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-xs italic">No tech detected</p>
                )}
              </Card>
            </div>

            {/* Page Sections */}
            {data.sections && data.sections.length > 0 && (
              <Card className="border-0 shadow-xl p-6">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Page Sections</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {data.sections.map((section, i) => (
                    <div key={i} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <p className="text-sm font-bold text-gray-900 mb-1">{section.heading}</p>
                      <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">{section.content}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* SEO */}
            <Card className="border-0 shadow-xl p-6">
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">SEO Metadata</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Page Title</p>
                  <p className="text-gray-800 font-medium text-sm">{data.seo.title || '—'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Language</p>
                  <p className="text-gray-800 font-medium text-sm">{data.seo.language || '—'}</p>
                </div>
                <div className="md:col-span-2 space-y-1">
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Meta Description</p>
                  <p className="text-gray-700 text-sm leading-relaxed">{data.seo.metaDescription || '—'}</p>
                </div>
                {data.seo.keywords.length > 0 && (
                  <div className="md:col-span-2 space-y-2">
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">Keywords</p>
                    <div className="flex flex-wrap gap-1.5">
                      {data.seo.keywords.map((k) => (
                        <span key={k} className="px-2 py-0.5 bg-[#216974]/10 text-[#216974] rounded text-xs font-medium">{k}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Links */}
            {data.links.length > 0 && (
              <Card className="border-0 shadow-xl p-6">
                <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Links Found ({data.links.length})</h3>
                <div className="grid md:grid-cols-2 gap-1 max-h-64 overflow-y-auto">
                  {data.links.map((link, i) => (
                    <a key={i} href={link.href} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 text-xs text-gray-600 hover:text-[#54c4b6] transition-colors group">
                      <Link2 size={10} className="text-gray-300 flex-shrink-0" />
                      <span className="truncate">{link.text || link.href}</span>
                      <ExternalLink size={9} className="text-gray-200 group-hover:text-[#54c4b6] flex-shrink-0 ml-auto" />
                    </a>
                  ))}
                </div>
              </Card>
            )}

          </motion.div>
        )}
      </AnimatePresence>

      <HistorySection toolId="web-intel" />
    </div>
  );
};

export default WebIntelligence;
